import React, { useState, useEffect, useRef, useContext, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import GlobalContext from '../../Context/Context'
import { useLanguage } from '../../Context/LanguageContext'
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher'
import logoLight from '../../Assets/img/imigo-logo.png'

const NAV_ITEMS = [
  { key: 'home', path: '/' },
  { key: 'articles', path: '/articles' },
  { key: 'businesses', path: '/businesses' },
  { key: 'events', path: '/events' },
  { key: 'marketplace', path: '/marketplace' },
  { key: 'about', path: '/about' },
  { key: 'contact', path: '/contact' },
]

const APP_LINKS = [
  { href: 'https://apps.apple.com/us/app/imigo-connect/id6744267446', labelKey: 'iosApp', icon: 'fab fa-apple' },
  { href: 'https://play.google.com/store/apps/details?id=app.imigo', labelKey: 'androidApp', icon: 'fab fa-google-play' },
]

const HeaderImigo = ({ topSpace }) => {
  const { setHeaderHeight } = useContext(GlobalContext)
  const { t, getLocalizedPath } = useLanguage()
  const location = useLocation()
  const headerRef = useRef(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  const closeMenu = useCallback(() => setMenuOpen(false), [])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    closeMenu()
  }, [location.pathname, closeMenu])

  useEffect(() => {
    document.body.classList.toggle('imigo-menu-open', menuOpen)
    return () => document.body.classList.remove('imigo-menu-open')
  }, [menuOpen])

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight)
      }
    }
    updateHeight()
    window.addEventListener('resize', updateHeight)
    return () => window.removeEventListener('resize', updateHeight)
  }, [setHeaderHeight, menuOpen])

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeMenu()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [closeMenu])

  const isActive = (path) => {
    const current = location.pathname.replace(/^\/(en|ar|fa)/, '') || '/'
    const normalized = path === '/' ? '/' : path
    return current === normalized || current.startsWith(`${normalized}/`)
  }

  return (
    <>
      <header
        ref={headerRef}
        className={`imigo-header ${topSpace ? 'top-space' : ''} ${scrolled ? 'is-scrolled' : ''}`}
      >
        <div className="imigo-header-bar">
          <Link
            aria-label="Imigo home"
            className="imigo-header-logo"
            to={getLocalizedPath('/')}
            onClick={closeMenu}
          >
            <img src={logoLight} alt="Imigo" />
          </Link>

          <nav className="imigo-header-nav-desktop" aria-label="Main navigation">
            <ul>
              {NAV_ITEMS.map(({ key, path }) => (
                <li key={key}>
                  <Link
                    to={getLocalizedPath(path)}
                    className={isActive(path) ? 'is-active' : ''}
                  >
                    {t(`menu.${key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="imigo-header-actions-desktop">
            <LanguageSwitcher variant="header" />
            {APP_LINKS.map(({ href, labelKey, icon }) => (
              <a
                key={href}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="imigo-header-app-btn"
              >
                <i className={icon} aria-hidden="true" />
                {t(`common.${labelKey}`)}
              </a>
            ))}
          </div>

          <button
            type="button"
            className={`imigo-header-toggle ${menuOpen ? 'is-open' : ''}`}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="imigo-mobile-menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </header>

      <button
        type="button"
        className={`imigo-mobile-backdrop ${menuOpen ? 'is-visible' : ''}`}
        aria-label="Close menu"
        onClick={closeMenu}
        tabIndex={menuOpen ? 0 : -1}
      />

      <aside
        id="imigo-mobile-menu"
        className={`imigo-mobile-menu ${menuOpen ? 'is-open' : ''}`}
        aria-hidden={!menuOpen}
      >
        <div className="imigo-mobile-menu-inner">
          <nav aria-label="Mobile navigation">
            <ul className="imigo-mobile-nav-list">
              {NAV_ITEMS.map(({ key, path }) => (
                <li key={key}>
                  <Link
                    to={getLocalizedPath(path)}
                    className={isActive(path) ? 'is-active' : ''}
                    onClick={closeMenu}
                  >
                    {t(`menu.${key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="imigo-mobile-menu-footer">
            <LanguageSwitcher variant="mobile" onSelect={closeMenu} />
            <div className="imigo-mobile-app-buttons">
              {APP_LINKS.map(({ href, labelKey, icon }) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="imigo-mobile-app-btn"
                  onClick={closeMenu}
                >
                  <i className={icon} aria-hidden="true" />
                  {t(`common.${labelKey}`)}
                </a>
              ))}
            </div>
          </div>
        </div>
      </aside>
    </>
  )
}

export default HeaderImigo
